import { LoyaltyEnum, type OrderType } from "@innoclouds/api";
import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuth } from "#/composables/useAuth";
import { presistData } from "#/payment/handlers/basePaymentHandler";
import type { IProduct } from "#/types/iCardProduct";
import type { PaymentConfig, PaymentContext, PaymentMethod } from "#/types/payment";
import { PaymentMethodRegistry } from "../payment/PaymentMethodRegistry";
import { createCartCleanupService } from "../payment/services/CartCleanupService";
import { createPaymentErrorHandler } from "../payment/services/PaymentErrorHandler";
import { createPaymentNavigationService } from "../payment/services/PaymentNavigationService";
import clearLocalStorage from "../utils/clearLocalStorage";
import { useMainStore } from "./main";
import { useModalStore } from "./modals";
import { useTableStore } from "./table";
import { useThemeStore } from "./theme";

export const useCartStore = defineStore("cartLib", () => {
	const router = useRouter();
	const route = useRoute();
	const themeStore = useThemeStore();
	const storeMain = useMainStore();
	const modals = useModalStore();
	const tableStore = useTableStore();
	const { config_lib, countBonus, totalPrice_lib, initialCartPrice_lib, is18Allowed } = storeToRefs(storeMain);
	const isDrawerOpen = ref(false);
	const error = ref("");
	const currentState = ref<"wait" | "success" | "not_found">("wait");
	const tabName = ref<string | null>(null);

	const isCreateButtonVisible = ref(true);
	const extraDiscount = ref(0);
	const linkQR = ref("");
	const orderTypesDevice = ref<OrderType[]>([]);
	const providerTypes = ref<any>([]);
	const orderItemCommentSuggestions = ref<string[]>([]);
	const selectedProvider = ref();
	const paymentCostQR = ref(0);
	const is_paymentCart = ref(false);
	const configPayment = ref<PaymentConfig>();

	const isAdultConfirmed = ref(false);

	const orderExternalId = ref<string | null>(null);
	const orderNumber = ref<number | null>(null);
	const orderError = ref([
		"failed_payment",
		"failed_after_paid",
		"failed",
		"failed_after_sent",
		"failed_after_sent_fiscalization",
		"payment_initiation_retry",
		"cancelled",
		"failed_after_payment_initiation",
	]);
	const cashPaymentAmount = ref<number>(0);

	const selectedOrderType = ref<OrderType | null>(null);
	const orderTypes = computed(() => tableStore.data?.branch?.order_types || orderTypesDevice.value || []);

	const isButtonDisabled = ref(false);
	const orderCompleted_lib = ref<number | null>(null);
	const count = ref(0);
	const isOrderCreating = ref(false);

	/**
	 * Снапшот marked-товаров корзины на момент клика «Оплатить».
	 * Используется в CartCompletedOrder для popup после оплаты (т.к. корзина
	 * чистится раньше, чем выставляется orderCompleted_lib).
	 */
	const markedItemsSnapshot = ref<IProduct[]>([]);

	const snapshotMarkedItems = () => {
		markedItemsSnapshot.value = storeMain.cartProducts_lib.filter((p) => p?.is_marked);
	};

	const clearMarkedItemsSnapshot = () => {
		markedItemsSnapshot.value = [];
	};

	const is_notification_lib = ref(false);
	const notificationData_lib = ref({
		textNotification: "",
	});

	const showNotification = (message: string) => {
		is_notification_lib.value = true;
		notificationData_lib.value.textNotification = message;
	};

	/**
	 * Получение текущего метода оплаты
	 * Возвращает selectedProvider?.front_type если есть выбор, иначе providerTypes[0]?.front_type
	 */
	const getCurrentPaymentMethodId = (): string | null => {
		if (selectedProvider.value?.front_type) {
			return selectedProvider.value.front_type;
		}

		return providerTypes.value[0]?.front_type || null;
	};

	/**
	 * Создание обработчика платежа и конфигурации
	 */
	const createPaymentHandler = (
		context: PaymentContext,
		is_requestKiosk: boolean,
		paymentMethodId?: string,
		locator?: string,
	): { handler: any; config: PaymentConfig } | null => {
		const id = paymentMethodId ?? providerTypes.value[0]?.front_type;

		if (!id) {
			console.warn("Не удалось определить метод оплаты");
			return null;
		}

		const methodConfig = PaymentMethodRegistry.getConfig(id);

		if (!methodConfig || !PaymentMethodRegistry.isMethodAvailable(id, context)) {
			console.warn(`Метод ${id} недоступен для контекста ${context}`);
			return null;
		}

		const handler = PaymentMethodRegistry.createHandler(id);
		if (!handler) {
			console.warn(`Не удалось создать обработчик для метода ${id}`);
			return null;
		}

		configPayment.value = createPaymentConfig(methodConfig.method, context, is_requestKiosk, locator);
		return { handler, config: configPayment.value };
	};

	const fullReset = () => {
		isCreateButtonVisible.value = true;
		isButtonDisabled.value = false;
		orderCompleted_lib.value = null;
		isOrderCreating.value = false;
		extraDiscount.value = 0;
		orderNumber.value = null;
		orderExternalId.value = null;
		cashPaymentAmount.value = 0;
		is_paymentCart.value = false;
		selectedProvider.value = undefined;
		linkQR.value = "";
	};

	/**
	 * Создание конфигурации для обработчика платежа
	 */
	const createPaymentConfig = (method: PaymentMethod, context: PaymentContext, is_requestKiosk: boolean, locator?: string): PaymentConfig => {
		const baseConfig: Omit<PaymentConfig, "cartCleanup" | "errorHandler" | "navigation"> = {
			method,
			context,
			is_requestKiosk,
			router,
			route,
			orderErrors: orderError.value,
			area: config_lib.value.area,
			totalPrice: totalPrice_lib,
			initialCartPrice: initialCartPrice_lib,
			locator,

			showNotification,
			deleteCart,
			resetStores,
			redirect: (path: string) => router.push(path),
			updateOrderStatus: (status: number | null) => {
				orderCompleted_lib.value = status;
			},
			setOrderNumber: (number: number) => {
				orderNumber.value = number;
			},
			setOrderExternalId: (id: string) => {
				orderExternalId.value = id;
			},
			loadCart: () => storeMain.loadCart(),
			resetBonuses: () => {
				countBonus.value = 0;
			},
		};

		// Создаем сервисы и добавляем их в конфиг
		const errorHandler = createPaymentErrorHandler();
		const cartCleanup = createCartCleanupService(baseConfig as PaymentConfig);
		const navigation = createPaymentNavigationService(baseConfig as PaymentConfig);

		return {
			...baseConfig,
			errorHandler,
			cartCleanup,
			navigation,
		};
	};

	const setState = (state: typeof currentState.value): void => {
		currentState.value = state;
	};

	const openDrawer = () => {
		if (storeMain.config_lib?.loyalty === LoyaltyEnum.LOYALTY_PLANT) return modals.open("loyalty-plant-scanner");

		if (storeMain.isLoyalSuccessScreen) {
			storeMain.isLoyalSuccessScreen = false;
		}
		if (themeStore.data?.kiosk_show_loyalty_card_button && themeStore.data?.kiosk_show_loyalty_phone_input_button) isDrawerOpen.value = true;
		else if (!themeStore.data?.kiosk_show_loyalty_card_button && themeStore.data?.kiosk_show_loyalty_phone_input_button) storeMain.is_OpenLoyal = true;
		else isDrawerOpen.value = true;
		currentState.value = "wait";
		error.value = "";
	};

	const closeDrawer = () => {
		isDrawerOpen.value = false;
		currentState.value = "wait";
		error.value = "";
	};

	const clearCard = () => {
		tabName.value = null;
		error.value = "";
		currentState.value = "wait";
		closeDrawer();
	};

	const resetStores = () => {
		isButtonDisabled.value = false;
		orderCompleted_lib.value = null;
		isOrderCreating.value = false;
	};

	const deleteCart = (path: string | null = null, clearTokens: boolean = false, withSave: boolean = false) => {
		const auth = useAuth();
		if (withSave) presistData(clearTokens, isAdultConfirmed.value);
		storeMain.cartProducts_lib = [];
		storeMain.totalPrice_lib = null;
		storeMain.is_OpenLoyal = false;
		if (orderCompleted_lib.value !== 7 && orderCompleted_lib.value !== null) {
			cashPaymentAmount.value = 0;
		}
		clearLocalStorage(["cart"]);
		if (clearTokens) {
			isAdultConfirmed.value = false;
			clearLocalStorage(["user"]);
			auth.clear();
			// storeMain.profile = null;
			sessionStorage.removeItem("is_adult");
			is18Allowed.value = false;
		}
		if (path) {
			router.push(`/${route.params.orgId}/${path}`);
		}
	};

	const setCashPaymentAmount = (amount: number) => {
		cashPaymentAmount.value = amount;
	};

	return {
		isButtonDisabled,
		orderCompleted_lib,
		isOrderCreating,
		extraDiscount,
		orderNumber,
		orderExternalId,
		cashPaymentAmount,
		is_notification_lib,
		notificationData_lib,
		orderItemCommentSuggestions,
		count,
		tabName,
		error,
		isDrawerOpen,
		currentState,
		isAdultConfirmed,
		isCreateButtonVisible,
		linkQR,
		orderTypes,
		orderTypesDevice,
		providerTypes,
		is_paymentCart,
		selectedOrderType,
		selectedProvider,
		configPayment,
		paymentCostQR,
		markedItemsSnapshot,

		fullReset,
		deleteCart,
		setState,
		openDrawer,
		closeDrawer,
		clearCard,

		showNotification,
		createPaymentHandler,
		getCurrentPaymentMethodId,
		snapshotMarkedItems,
		clearMarkedItemsSnapshot,
		setCashPaymentAmount,

		getAvailablePaymentMethods: (context: PaymentContext) => PaymentMethodRegistry.getAvailableMethods(context),
	};
});
